import { LoadingOutlined, SearchOutlined } from "@ant-design/icons"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { DebouncedInput } from "../../components/Input/DebouncedInput.tsx"
import { cn } from "../../utils/cn.ts"
import { getSearchResults } from "./api/global-map.api.ts"
import { EntityIcon } from "./map/EntityIcon.tsx"
import type {
  LeadQuickView,
  ProjectQuickView
} from "./redux/global-map.types.ts"
import { emitMapEvent } from "./telemetry.ts"

// The spec's own numbers: search after 2+ characters, ~300ms debounce — a
// dedicated, shorter delay than this app's usual `SEARCH_DEBOUNCE_MS` (700ms,
// `DebouncedInput`'s own default), which is tuned for a filter re-fetch, not
// a live autocomplete panel a user is actively watching populate.
const MIN_QUERY_LENGTH = 2
const SEARCH_DELAY_MS = 300

type QuickView = ProjectQuickView | LeadQuickView

// Flattened so keyboard Up/Down and the highlighted index can walk Projects,
// then Leads, then the trailing "View all results" row as one linear list —
// exactly the shape `aria-activedescendant`-style listbox navigation needs.
type SearchRow = { kind: "result"; result: QuickView } | { kind: "view-all" }

interface GlobalRecordSearchInputProps {
  // Fires with the record's own quick-view payload (already fully fetched —
  // no second detail request needed) once the caller picks a suggestion.
  onSelectResult: (result: QuickView) => void
  // Fires on Enter (with nothing highlighted) or a "View all results" click.
  // `MapTab` narrows the map to this query — see its own call site comment
  // for why that stands in for a real List-tab destination this app doesn't
  // have yet.
  onViewAllResults: (query: string) => void
  // Bump to empty the box and close the panel — `DebouncedInput`'s own
  // established convention for "a value change can't be read off the value
  // alone" (clearing a field that already reads empty is no change at all).
  // Needed here for a different reason than `DebouncedInput` needs it for:
  // this component's `draft` is fully local, never fed by Redux's own
  // `searchTerm`, so "Clear All"/"View All" resetting `searchTerm` has no
  // way to reach this box's own displayed text without an explicit signal —
  // a real, reported bug (the input kept showing the old query after
  // either button was clicked, even once the map itself had reset).
  resetSignal?: number
}

function suggestionLabel(result: QuickView): string {
  const parts = [
    result.title ?? "",
    result.reference,
    "customer" in result.people ? result.people.customer : undefined,
    result.location.address ?? undefined
  ].filter((part): part is string => Boolean(part))
  return parts.join(" — ")
}

function SuggestionRow({
  result,
  highlighted,
  onSelect
}: {
  result: QuickView
  highlighted: boolean
  onSelect: () => void
}) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      role="option"
      aria-selected={highlighted}
      onMouseDown={(event) => {
        // `mousedown`, not `click` — fires before the input's own `blur`
        // closes the panel, the same trap `AddressAutoComplete`'s own clear
        // icon and this app's other popup-over-a-field controls guard
        // against.
        event.preventDefault()
        onSelect()
      }}
      className={cn(
        "flex w-full flex-col gap-0.5 px-3 py-2 text-left text-xs",
        highlighted ? "bg-slate-100" : "hover:bg-slate-50"
      )}
    >
      <span className="flex items-center gap-1.5 font-medium text-slate-800">
        <EntityIcon entityType={result.entity_type} className="size-4" />
        <span className="truncate">
          {result.title || suggestionLabel(result)}
        </span>
        <span className="shrink-0 text-slate-400">{result.reference}</span>
      </span>
      <span className="truncate pl-5 text-slate-500">
        {[
          result.entity_type === "project" ? result.project_type : null,
          "customer" in result.people ? result.people.customer : null,
          result.location.address,
          result.status_label
        ]
          .filter(Boolean)
          .join(" · ") || t("globalMap.recordSearch.placeholder")}
      </span>
    </button>
  )
}

/**
 * The Global Map unified search — one box above the title matching Leads
 * and Projects across name/record-number/address/city/state/zip/project
 * type/customer/salesperson/project-manager/subcontractor, per an explicit
 * product spec. Replaces the earlier deliberately-unwired placeholder (see
 * git history for that version's own docstring) now that the spec's exact
 * behavior — debounce, minimum length, grouped suggestions, keyboard nav,
 * "View all results" — is known.
 *
 * Stays local-state-only for the suggestion panel itself (draft text,
 * open/closed, the fetched rows) rather than routing through Redux — this
 * is ephemeral UI state scoped to one input, not shared app state anything
 * else reads. Selecting a result or committing "View all" is handed to the
 * caller (`MapTab`) via props, since THAT is where the shared map/selection
 * state actually lives.
 */
export function GlobalRecordSearchInput({
  onSelectResult,
  onViewAllResults,
  resetSignal
}: GlobalRecordSearchInputProps) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState<string | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errored, setErrored] = useState(false)
  const [projects, setProjects] = useState<ProjectQuickView[]>([])
  const [leads, setLeads] = useState<LeadQuickView[]>([])
  const [projectTotal, setProjectTotal] = useState(0)
  const [leadTotal, setLeadTotal] = useState(0)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  // Monotonic guard against a stale response overwriting a newer one — the
  // same "later dispatched wins" shape `global-map.slice.ts`'s own
  // `requestId` guard uses for `fetchMapFeatures`, reimplemented locally
  // since this panel's fetch never touches Redux.
  const requestIdRef = useRef(0)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const lastResetSignal = useRef(resetSignal)

  const label = t("globalMap.recordSearch.placeholder")

  // "Clear All"/"View All" (`MapTab`) both reset Redux's `searchTerm`, which
  // this component never reads from — see this component's own prop
  // docstring for why an explicit signal is what closes the loop back to
  // this box's own displayed text.
  useEffect(() => {
    if (resetSignal === lastResetSignal.current) return
    lastResetSignal.current = resetSignal
    requestIdRef.current += 1
    setDraft(null)
    setIsOpen(false)
    setLoading(false)
    setErrored(false)
    setProjects([])
    setLeads([])
    setProjectTotal(0)
    setLeadTotal(0)
    setHighlightedIndex(-1)
  }, [resetSignal])

  const runSearch = useCallback((query: string) => {
    const trimmed = query.trim()
    if (trimmed.length < MIN_QUERY_LENGTH) {
      requestIdRef.current += 1
      setLoading(false)
      setErrored(false)
      setProjects([])
      setLeads([])
      setProjectTotal(0)
      setLeadTotal(0)
      setIsOpen(false)
      return
    }

    const requestId = ++requestIdRef.current
    setLoading(true)
    setErrored(false)

    getSearchResults({ search: trimmed })
      .then((response) => {
        if (requestId !== requestIdRef.current) return
        if (!response.ok || !response.data) {
          setErrored(true)
          setProjects([])
          setLeads([])
          emitMapEvent("search_error", {})
          return
        }
        setProjects(response.data.projects)
        setLeads(response.data.leads)
        setProjectTotal(response.data.project_total)
        setLeadTotal(response.data.lead_total)
        setHighlightedIndex(-1)
        setIsOpen(true)
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return
        setErrored(true)
        emitMapEvent("search_error", {})
      })
      .finally(() => {
        if (requestId !== requestIdRef.current) return
        setLoading(false)
      })
  }, [])

  // `DebouncedInput`'s own `onValueChange` fires only after `SEARCH_DELAY_MS`
  // of no typing — no second debounce layer needed here. `runSearch` is
  // stable (empty dep array), so `draft` is the only real dependency.
  useEffect(() => {
    runSearch(draft ?? "")
  }, [draft, runSearch])

  const rows = useMemo<SearchRow[]>(() => {
    const resultRows: SearchRow[] = [...projects, ...leads].map((result) => ({
      kind: "result" as const,
      result
    }))
    const shown = projects.length + leads.length
    const total = projectTotal + leadTotal
    return total > shown
      ? [...resultRows, { kind: "view-all" as const }]
      : resultRows
  }, [projects, leads, projectTotal, leadTotal])

  const handleSelectRow = useCallback(
    (row: SearchRow) => {
      if (row.kind === "view-all") {
        emitMapEvent("view_all_selected", {})
        onViewAllResults((draft ?? "").trim())
      } else {
        emitMapEvent("result_selected", { source: "search" })
        onSelectResult(row.result)
      }
      setIsOpen(false)
    },
    [draft, onSelectResult, onViewAllResults]
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Escape") {
        setIsOpen(false)
        return
      }
      if (event.key === "ArrowDown") {
        if (!rows.length) return
        event.preventDefault()
        setIsOpen(true)
        setHighlightedIndex((index) => Math.min(index + 1, rows.length - 1))
        return
      }
      if (event.key === "ArrowUp") {
        if (!rows.length) return
        event.preventDefault()
        setHighlightedIndex((index) => Math.max(index - 1, 0))
        return
      }
      if (event.key === "Enter") {
        if (highlightedIndex >= 0 && rows[highlightedIndex]) {
          event.preventDefault()
          handleSelectRow(rows[highlightedIndex])
          return
        }
        const trimmed = (draft ?? "").trim()
        if (trimmed.length >= MIN_QUERY_LENGTH) {
          event.preventDefault()
          emitMapEvent("view_all_selected", {})
          onViewAllResults(trimmed)
          setIsOpen(false)
        }
      }
    },
    [rows, highlightedIndex, draft, handleSelectRow, onViewAllResults]
  )

  useEffect(() => {
    if (!isOpen) return
    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", handlePointerDown)
    return () => document.removeEventListener("mousedown", handlePointerDown)
  }, [isOpen])

  const viewAllIndex = rows.length - 1
  const isViewAllHighlighted =
    rows.length > 0 &&
    rows[viewAllIndex]?.kind === "view-all" &&
    highlightedIndex === viewAllIndex

  return (
    <div
      ref={containerRef}
      className="relative max-w-md"
      data-testid="global-map-record-search"
    >
      <DebouncedInput
        value={draft}
        onValueChange={setDraft}
        delay={SEARCH_DELAY_MS}
        prefix={
          loading ? (
            <LoadingOutlined aria-hidden spin />
          ) : (
            <SearchOutlined aria-hidden />
          )
        }
        aria-label={label}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        placeholder={label}
        className="rounded-lg bg-white shadow-sm"
        data-testid="global-map-record-search-input"
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (rows.length) setIsOpen(true)
        }}
      />
      {isOpen && (
        <div
          role="listbox"
          aria-label={label}
          className="absolute left-0 top-full z-10 mt-1 max-h-96 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
          data-testid="global-map-record-search-panel"
        >
          {errored ? (
            <div className="flex items-center justify-between px-3 py-2 text-xs text-slate-500">
              {t("globalMap.recordSearch.error")}
              <button
                type="button"
                className="font-medium text-blue-600"
                onMouseDown={(event) => {
                  event.preventDefault()
                  runSearch(draft ?? "")
                }}
              >
                {t("globalMap.recordSearch.retry")}
              </button>
            </div>
          ) : rows.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-500">
              {t("globalMap.recordSearch.noResults")}
            </div>
          ) : (
            <>
              {projects.length > 0 && (
                <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  {t("globalMap.recordSearch.projectsGroup")}
                </div>
              )}
              {projects.map((project, index) => (
                <SuggestionRow
                  key={project.id}
                  result={project}
                  highlighted={highlightedIndex === index}
                  onSelect={() =>
                    handleSelectRow({ kind: "result", result: project })
                  }
                />
              ))}
              {leads.length > 0 && (
                <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  {t("globalMap.recordSearch.leadsGroup")}
                </div>
              )}
              {leads.map((lead, index) => (
                <SuggestionRow
                  key={lead.id}
                  result={lead}
                  highlighted={highlightedIndex === projects.length + index}
                  onSelect={() =>
                    handleSelectRow({ kind: "result", result: lead })
                  }
                />
              ))}
              {rows[viewAllIndex]?.kind === "view-all" && (
                <button
                  type="button"
                  role="option"
                  aria-selected={isViewAllHighlighted}
                  onMouseDown={(event) => {
                    event.preventDefault()
                    handleSelectRow({ kind: "view-all" })
                  }}
                  className={cn(
                    "w-full border-t border-slate-100 px-3 py-2 text-left text-xs font-medium text-blue-600",
                    isViewAllHighlighted ? "bg-slate-100" : "hover:bg-slate-50"
                  )}
                >
                  {t("globalMap.recordSearch.viewAllResults", {
                    count: projectTotal + leadTotal
                  })}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default GlobalRecordSearchInput
